import { useQuery } from '@tanstack/react-query';
import { useSession } from './useSession';
import { useQueryCreator } from './useQueryCreator';
import { partnerManagementService } from '~/services';
import { getCarrierEulaConfig, type LegalAgreementType } from '~/config/carrierEulaConfig';

/**
 * Pure acceptance check: a partner has a valid acceptance when the latest record is accepted AND,
 * for versioned agreement types, the accepted version matches the currently-required version.
 * A null/undefined required version means the type is version-agnostic (legacy behavior).
 */
export const isLegalAgreementAccepted = (
  agreement: { accepted?: boolean; eulaVersion?: string } | null | undefined,
  requiredVersion: string | undefined,
): boolean => {
  const isAccepted = agreement?.accepted ?? false;
  const versionSatisfied = requiredVersion == null || agreement?.eulaVersion === requiredVersion;
  return isAccepted && versionSatisfied;
};

export interface LegalAgreementAcceptedResult {
  /**
   * True only when the partner has accepted the agreement AND, for versioned agreement types, the
   * accepted version matches the currently-required version. A version mismatch returns false,
   * which drives re-acceptance after a EULA version bump.
   */
  accepted: boolean;
  isLoading: boolean;
  /**
   * True when the acceptance lookup failed (a non-404 error). Callers should surface this rather
   * than treat it as "not accepted" — otherwise a transient fetch failure looks like the partner
   * never accepted.
   */
  isError: boolean;
  /** The version the partner last accepted, if any. */
  acceptedVersion?: string;
  /** The version currently required for this type (undefined = version-agnostic). */
  requiredVersion?: string;
}

/**
 * Returns whether the current partner has a valid acceptance of the given legal-agreement type,
 * accounting for the required EULA version configured in the carrier EULA registry.
 *
 * Pass `undefined` for `type` (e.g. an unrecognized route param) to disable the lookup entirely —
 * no request is made and the result is a benign "not accepted, not loading, not error".
 */
export const useLegalAgreementAccepted = (type: LegalAgreementType | undefined): LegalAgreementAcceptedResult => {
  const { sessionPartnerId } = useSession();
  const requiredVersion = type ? getCarrierEulaConfig(type).eulaVersion : undefined;

  const legalAgreementQuery = useQuery({
    ...useQueryCreator(partnerManagementService.legalAgreements.get, {
      // `type` only feeds the query key here; the fetch is gated off (below) when it's undefined,
      // so the cast never reaches the network.
      args: [sessionPartnerId, type as LegalAgreementType],
    }),
    enabled: !!sessionPartnerId && !!type,
  });

  const data = legalAgreementQuery.data;

  return {
    accepted: isLegalAgreementAccepted(data, requiredVersion),
    isLoading: legalAgreementQuery.isLoading,
    isError: legalAgreementQuery.isError,
    acceptedVersion: data?.eulaVersion,
    requiredVersion,
  };
};
